/**
 * GridCrash — Versus Mode (CPU Opponent + Match Resolution)
 *
 * Competitive play against a simulated opponent. The CPU does NOT play a real
 * board — it runs a calibrated time-based simulation that races the human.
 * This is intentional:
 *   - Boards differ between players (each picks their own first click), so there
 *     is no shared board to sync. A match only needs SCORE + PROGRESS + TIME.
 *   - A full client-side minesweeper solver would be heavy and pointless when the
 *     match is decided by score, which we can model directly.
 *
 * ── Match resolution is SCORE-BASED, not a race-to-clear ──
 *   Final score decides the match. Blowing up does NOT auto-lose.
 *     - Blow up → score = (correct flags × 10) − (wrong flags × 5)
 *                 (no win bonus, no speed bonus — you locked in what you had)
 *     - Clear   → score = base + win bonus + speed bonus (full formula)
 *   Higher score wins. A careful blow-up can beat a sloppy clear.
 *   Tiebreak (equal score, e.g. both cleared flawlessly): faster time wins.
 *
 * The CPU therefore accumulates a SCORE over the match:
 *   - It flags mines over time at a skill-dependent rate (each correct flag = +10)
 *   - It occasionally places a wrong flag (each = −5), more often at low skill
 *   - It may detonate (lossChance), locking in its partial score
 *   - If it survives to its solve time, it "clears" and earns win + speed bonus
 *
 * Design note on the 5% rule:
 *   "willClear" is rolled separately from solve time. An Expert CPU drawing a
 *   perfectly average ~240s solve time still detonates 5% of the time — giving a
 *   careful human a real shot on score even when out-paced.
 */

// ─── Skill profiles ─────────────────────────────────────────────────────────

export type CpuSkill = "easy" | "medium" | "hard";

interface CpuProfile {
  label:          string;
  meanSolveSecs:  number;   // average time to clear the board
  stdDevSecs:     number;   // spread around the mean
  minSolveSecs:   number;   // floor (no superhuman instant clears)
  lossChance:     number;   // probability the CPU detonates instead of clearing
  mineCount:      number;   // mines on this difficulty (each correct flag = +10)
  winBonus:       number;   // bonus earned on a clean clear (matches engine.ts)
  maxSpeedBonus:  number;   // best-case speed bonus (matches engine.ts top tier)
  wrongFlagRate:  number;   // expected wrong flags over a full game (−5 each)
}

/**
 * Numbers mirror engine.ts scoring so the CPU's score is on the same scale as
 * the human's. mineCount / winBonus / speed match the DIFFICULTIES table.
 *   Easy   (9×9, 10):  win +100, fast solves 15–55s, sloppy (more wrong flags)
 *   Medium (16×16,40): win +250, solves 80–180s
 *   Expert (16×30,99): win +500, solves 150–350s, very few wrong flags
 * Loss chance drops as skill rises; Expert is pinned to the requested 5%.
 */
const CPU_PROFILES: Record<CpuSkill, CpuProfile> = {
  easy: {
    label: "Rookie Bot",  meanSolveSecs: 38,  stdDevSecs: 14, minSolveSecs: 14,
    lossChance: 0.15, mineCount: 10, winBonus: 100, maxSpeedBonus: 100, wrongFlagRate: 1.6,
  },
  medium: {
    label: "Veteran Bot", meanSolveSecs: 125, stdDevSecs: 32, minSolveSecs: 55,
    lossChance: 0.10, mineCount: 40, winBonus: 250, maxSpeedBonus: 100, wrongFlagRate: 1.2,
  },
  hard: {
    label: "Master Bot",  meanSolveSecs: 240, stdDevSecs: 55, minSolveSecs: 95,
    lossChance: 0.05, mineCount: 99, winBonus: 500, maxSpeedBonus: 100, wrongFlagRate: 0.7,
  },
};

// ─── Types ──────────────────────────────────────────────────────────────────

export interface CpuSnapshot {
  progress:    number;   // 0..1 fraction of the board cleared
  elapsedSecs: number;
  score:       number;   // live score (correct flags ×10 − wrong flags ×5, + bonuses on clear)
  alive:       boolean;  // false once the CPU has detonated
  finished:    boolean;  // true once the CPU has cleared its board
  skill:       CpuSkill;
  label:       string;
}

export type MatchResult = "human_win" | "cpu_win" | "draw";

export interface MatchDetail {
  result:        MatchResult;
  humanScore:    number;
  cpuScore:      number;
  humanTimeSecs: number | null;  // null if the human did not finish
  cpuTimeSecs:   number | null;  // null if the CPU did not finish
  humanCleared:  boolean;
  cpuCleared:    boolean;
  cpuLabel:      string;
  cpuSkill:      CpuSkill;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Box-Muller transform — one standard-normal sample, scaled to mean/stdDev. */
function gaussian(mean: number, stdDev: number): number {
  let u = 0;
  let v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  const z = Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  return mean + z * stdDev;
}

// ─── CPU Opponent ─────────────────────────────────────────────────────────────

/**
 * A single simulated opponent. Construct it (which locks in the predetermined
 * outcome, solve time, and wrong-flag count), wire up onUpdate / onResolve,
 * then call start().
 *
 * Scoring mirrors engine.ts so the CPU competes on the human's scale:
 *   live  = (correctFlagsSoFar × 10) − (wrongFlagsSoFar × 5)
 *   clear = live + winBonus + speedBonus
 */
export class CpuOpponent {
  readonly skill: CpuSkill;
  private readonly profile: CpuProfile;

  private readonly willClear: boolean;
  private readonly targetSolveSecs: number;
  private readonly detonateSecs: number | null;

  // Predetermined flag plan
  private readonly totalWrongFlags: number;   // wrong flags placed over the game
  private readonly wrongFlagTimes: number[];   // seconds at which each wrong flag lands

  private startTime: number | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private finished = false;
  private alive = true;

  // Live tallies
  private correctFlags = 0;
  private wrongFlags = 0;
  private finalScore = 0;

  public onUpdate:  ((snap: CpuSnapshot) => void) | null = null;
  public onResolve: ((outcome: "cpu_win" | "cpu_lose") => void) | null = null;

  constructor(skill: CpuSkill) {
    this.skill   = skill;
    this.profile = CPU_PROFILES[skill];

    // Roll the outcome independently of solve time (this is the 5% rule).
    this.willClear = Math.random() > this.profile.lossChance;

    // Draw a solve time from a clamped Gaussian.
    const raw = gaussian(this.profile.meanSolveSecs, this.profile.stdDevSecs);
    this.targetSolveSecs = Math.max(this.profile.minSolveSecs, Math.round(raw));

    if (this.willClear) {
      this.detonateSecs = null;
    } else {
      // Loss-game: detonate somewhere between 30% and 90% of its solve time.
      const frac = 0.3 + Math.random() * 0.6;
      this.detonateSecs = Math.round(this.targetSolveSecs * frac);
    }

    // Wrong flags: Poisson-ish — round a noisy sample of the expected rate.
    const wrongRaw = Math.max(0, gaussian(this.profile.wrongFlagRate, this.profile.wrongFlagRate * 0.5));
    this.totalWrongFlags = Math.round(wrongRaw);

    // Scatter the wrong-flag moments across the active part of the game.
    const activeWindow = this.detonateSecs ?? this.targetSolveSecs;
    this.wrongFlagTimes = [];
    for (let i = 0; i < this.totalWrongFlags; i++) {
      this.wrongFlagTimes.push(Math.random() * activeWindow);
    }
    this.wrongFlagTimes.sort((a, b) => a - b);
  }

  get label(): string { return this.profile.label; }
  get estimatedSolveSecs(): number { return this.targetSolveSecs; }
  get score(): number { return this.finalScore; }

  start(): void {
    if (this.timer !== null) return;
    this.startTime = Date.now();
    this.timer = setInterval(() => this.tick(), 250);
  }

  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /** Live score from current tallies (no bonuses — those land only on a clear). */
  private liveScore(): number {
    return Math.max(0, this.correctFlags * 10 - this.wrongFlags * 5);
  }

  private tick(): void {
    if (this.startTime === null || this.finished || !this.alive) return;
    const elapsed = (Date.now() - this.startTime) / 1000;

    // Progress drives correct-flag accumulation: by solve time, all mines flagged.
    const progress = Math.min(elapsed / this.targetSolveSecs, 1);
    this.correctFlags = Math.min(
      this.profile.mineCount,
      Math.floor(progress * this.profile.mineCount)
    );

    // Apply any wrong flags whose scheduled moment has passed.
    while (
      this.wrongFlags < this.totalWrongFlags &&
      this.wrongFlagTimes[this.wrongFlags] <= elapsed
    ) {
      this.wrongFlags++;
    }

    // Loss-game: detonate at the predetermined moment.
    // Speed bonus still applies — being fast matters even if you blow up.
    if (!this.willClear && this.detonateSecs !== null && elapsed >= this.detonateSecs) {
      this.alive = false;
      const speedBonus = this.computeSpeedBonus(this.detonateSecs);
      this.finalScore = Math.max(0, this.liveScore() + speedBonus);
      this.emit(elapsed, Math.min(progress, 0.99));
      this.stop();
      this.onResolve?.("cpu_lose");
      return;
    }

    // Win-game: clear the board exactly at the target solve time, earning bonuses.
    if (this.willClear && elapsed >= this.targetSolveSecs) {
      this.finished = true;
      this.correctFlags = this.profile.mineCount;
      const speedBonus = this.computeSpeedBonus(this.targetSolveSecs);
      this.finalScore = this.liveScore() + this.profile.winBonus + speedBonus;
      this.emit(this.targetSolveSecs, 1);
      this.stop();
      this.onResolve?.("cpu_win");
      return;
    }

    // Still racing.
    this.finalScore = this.liveScore();
    this.emit(elapsed, Math.min(progress, 0.99));
  }

  /**
   * Mirror of engine.ts speed tiers, simplified: the CPU's "speed bonus" scales
   * with how close its solve time is to a strong time. We approximate with the
   * profile's mean as the reference — solving at/under mean earns full bonus,
   * tapering to 0 at ~2× mean.
   */
  private computeSpeedBonus(solveSecs: number): number {
    const mean = this.profile.meanSolveSecs;
    if (solveSecs <= mean) return this.profile.maxSpeedBonus;
    const taper = 1 - (solveSecs - mean) / mean; // 1 at mean → 0 at 2×mean
    return Math.max(0, Math.round(this.profile.maxSpeedBonus * taper));
  }

  private emit(elapsedSecs: number, progress: number): void {
    this.onUpdate?.({
      progress,
      elapsedSecs: Math.round(elapsedSecs),
      score: this.finalScore,
      alive: this.alive,
      finished: this.finished,
      skill: this.skill,
      label: this.profile.label,
    });
  }
}

// ─── Versus Match Controller ────────────────────────────────────────────────

/**
 * Orchestrates one human-vs-CPU match. The human side is driven by the existing
 * game engine in main.ts — call humanWon() / humanLost() with the human's final
 * score from the engine's WIN / LOSE transitions. The CPU side runs autonomously.
 *
 * Unlike a pure race, the CPU finishing FIRST does not end the match — the human
 * plays out their own board, then we compare final scores. The match resolves
 * once BOTH sides have a final score locked in.
 *
 * Resolution (score-based):
 *   - Higher final score wins.
 *   - A blow-up locks in (correct ×10 − wrong ×5); a clear adds win + speed bonus.
 *   - Tiebreak on equal score: faster finisher wins; if neither cleared, draw.
 */
export class VersusMatch {
  private readonly cpu: CpuOpponent;
  private ended = false;

  // CPU final state (locked when the CPU resolves)
  private cpuDone = false;
  private cpuCleared = false;
  private cpuScore = 0;
  private cpuTimeSecs: number | null = null;

  // Human final state (locked when humanWon/humanLost is called)
  private humanDone = false;
  private humanCleared = false;
  private humanScore = 0;
  private humanTimeSecs: number | null = null;

  public onOpponentUpdate: ((snap: CpuSnapshot) => void) | null = null;
  public onMatchEnd:       ((detail: MatchDetail) => void) | null = null;

  constructor(skill: CpuSkill) {
    this.cpu = new CpuOpponent(skill);

    this.cpu.onUpdate = (snap) => {
      this.onOpponentUpdate?.(snap);
    };

    this.cpu.onResolve = (outcome) => {
      this.cpuDone     = true;
      this.cpuCleared  = outcome === "cpu_win";
      this.cpuScore    = this.cpu.score;
      this.cpuTimeSecs = this.cpu.estimatedSolveSecs;
      this.tryResolve();
    };
  }

  get opponentLabel(): string { return this.cpu.label; }
  get opponentEstimateSecs(): number { return this.cpu.estimatedSolveSecs; }

  start(): void { this.cpu.start(); }

  /** Call when the human clears their board, with the engine's final score. */
  humanWon(humanScore: number, humanTimeSecs: number): void {
    if (this.humanDone) return;
    this.humanDone     = true;
    this.humanCleared  = true;
    this.humanScore    = humanScore;
    this.humanTimeSecs = humanTimeSecs;
    this.tryResolve();
  }

  /** Call when the human detonates, with the engine's locked-in (bonus-free) score. */
  humanLost(humanScore: number, humanTimeSecs: number): void {
    if (this.humanDone) return;
    this.humanDone     = true;
    this.humanCleared  = false;
    this.humanScore    = humanScore;
    this.humanTimeSecs = humanTimeSecs;
    this.tryResolve();
  }

  /** Force-stop (e.g. player quits or starts a new game). No result emitted. */
  abort(): void {
    this.ended = true;
    this.cpu.stop();
  }

  /** Resolve only once BOTH sides are done. */
  private tryResolve(): void {
    if (this.ended) return;
    if (!this.humanDone || !this.cpuDone) return;

    this.ended = true;
    this.cpu.stop();

    const result = this.decide();
    this.onMatchEnd?.({
      result,
      humanScore:    this.humanScore,
      cpuScore:      this.cpuScore,
      humanTimeSecs: this.humanTimeSecs,
      cpuTimeSecs:   this.cpuTimeSecs,
      humanCleared:  this.humanCleared,
      cpuCleared:    this.cpuCleared,
      cpuLabel:      this.cpu.label,
      cpuSkill:      this.cpu.skill,
    });
  }

  private decide(): MatchResult {
    if (this.humanScore > this.cpuScore) return "human_win";
    if (this.cpuScore > this.humanScore) return "cpu_win";

    // Equal score — tiebreak on time, but only meaningful if both cleared.
    if (this.humanCleared && this.cpuCleared) {
      const ht = this.humanTimeSecs ?? Infinity;
      const ct = this.cpuTimeSecs ?? Infinity;
      if (ht < ct) return "human_win";
      if (ct < ht) return "cpu_win";
    }
    return "draw";
  }
}

// ─── Pure resolver (exported for unit testing / reuse) ──────────────────────

export function resolveMatch(params: {
  humanScore:    number;
  cpuScore:      number;
  humanCleared:  boolean;
  cpuCleared:    boolean;
  humanTimeSecs: number;
  cpuTimeSecs:   number;
}): MatchResult {
  const { humanScore, cpuScore, humanCleared, cpuCleared, humanTimeSecs, cpuTimeSecs } = params;
  if (humanScore > cpuScore) return "human_win";
  if (cpuScore > humanScore) return "cpu_win";
  // Equal score — tiebreak on time only if both cleared.
  if (humanCleared && cpuCleared) {
    if (humanTimeSecs < cpuTimeSecs) return "human_win";
    if (cpuTimeSecs < humanTimeSecs) return "cpu_win";
  }
  return "draw";
}